import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import clsx from 'clsx';
import type { QueryResult, ResultRow } from '@idas/shared';

/**
 * The detail half of a dashboard: every row the query returned, with the
 * operations people immediately reach for — find, order, page.
 *
 * All three run in the browser, over the rows already fetched. That is a
 * deliberate limit rather than an oversight: pushing them down to SQL would
 * mean rewriting the analyst's statement, and a dashboard that quietly edits
 * the query it is showing is one nobody can reason about. The row cap is
 * stated in the footer so the difference between "no matches" and "no matches
 * in the first 10,000 rows" is never hidden.
 */

const PAGE_SIZES = [25, 50, 100, 250];

type Direction = 'asc' | 'desc';

/** Numbers sort as numbers, dates as dates, everything else as text. */
function compare(a: ResultRow[number], b: ResultRow[number]): number {
  if (a === null && b === null) return 0;
  // Nulls sort last in both directions — they are absence, not a low value.
  if (a === null) return 1;
  if (b === null) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function DataTable({ result }: { result: QueryResult }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ index: number; direction: Direction } | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return result.rows;
    return result.rows.filter((row) =>
      row.some((cell) => cell !== null && String(cell).toLowerCase().includes(needle)),
    );
  }, [result.rows, search]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    // Copied before sorting: mutating would reorder the source rows and make
    // "clear the sort" impossible.
    const rows = [...filtered];
    rows.sort((a, b) => {
      const result = compare(a[sort.index] ?? null, b[sort.index] ?? null);
      return sort.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pageCount - 1);
  const start = current * pageSize;
  const rows = sorted.slice(start, start + pageSize);

  const toggleSort = (index: number) => {
    setPage(0);
    setSort((s) =>
      // Third click clears it, so the original order is always reachable.
      !s || s.index !== index
        ? { index, direction: 'asc' }
        : s.direction === 'asc'
          ? { index, direction: 'desc' }
          : null,
    );
  };

  return (
    // Sized by its rows rather than by whatever space is left over. As a
    // flex child it collapsed to a row or two whenever a chart sat above
    // it, and its own scrollbar then stopped the page from scrolling past.
    <section className="panel flex shrink-0 flex-col rounded-lg">
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-edge px-3 py-2">
        <h2 className="text-xs font-semibold text-ink">Data</h2>
        <span className="text-2xs text-ink-muted">
          {sorted.length.toLocaleString()}
          {sorted.length !== result.rows.length && ` of ${result.rows.length.toLocaleString()}`} row
          {sorted.length === 1 ? '' : 's'}
        </span>

        <div className="flex-1" />

        <div className="relative w-56">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
          <input
            className="input py-1 pl-7"
            placeholder="Search all columns"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </div>

        <select
          className="input w-auto py-1"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      </header>

      {/* Horizontal only: a wide result still scrolls sideways inside its
          own box, but vertical scrolling belongs to the page so the last
          row is always reachable. */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-2xs">
          <thead>
            <tr>
              {result.columns.map((column, index) => {
                const active = sort?.index === index;
                return (
                  <th
                    key={`${column.name}-${index}`}
                    className="sticky top-0 z-10 whitespace-nowrap border-b border-edge bg-surface-raised px-2 py-1.5 text-left"
                  >
                    <button
                      className="flex items-center gap-1 font-medium text-ink-muted hover:text-ink"
                      onClick={() => toggleSort(index)}
                      title={`Sort by ${column.name}`}
                    >
                      {column.name}
                      {column.masked && (
                        <span className="text-lock" title="Masked by policy">
                          •
                        </span>
                      )}
                      {active &&
                        (sort!.direction === 'asc' ? (
                          <ArrowUp className="h-2.5 w-2.5 text-accent" />
                        ) : (
                          <ArrowDown className="h-2.5 w-2.5 text-accent" />
                        ))}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={start + i} className="border-b border-edge/50 hover:bg-surface-sunken">
                {result.columns.map((_, j) => (
                  <td
                    key={j}
                    className={clsx(
                      'max-w-[22rem] truncate px-2 py-1 text-ink',
                      typeof row[j] === 'number' && 'text-right tabular-nums',
                    )}
                    title={row[j] === null ? undefined : String(row[j])}
                  >
                    {row[j] === null ? <span className="text-ink-faint">null</span> : String(row[j])}
                  </td>
                ))}
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={result.columns.length}
                  className="px-3 py-8 text-center text-2xs text-ink-muted"
                >
                  {search ? `Nothing matches “${search}” in these rows.` : 'No rows.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-edge px-3 py-1.5 text-2xs text-ink-faint">
        <span>
          {sorted.length === 0
            ? 'No rows'
            : `${(start + 1).toLocaleString()}–${Math.min(start + pageSize, sorted.length).toLocaleString()} of ${sorted.length.toLocaleString()}`}
        </span>

        {result.truncated && (
          <span className="text-warn">
            · the query hit its {result.effectiveLimit.toLocaleString()}-row limit, so these are not
            all the rows that exist
          </span>
        )}

        <div className="flex-1" />

        <button
          className="btn btn-ghost px-1.5"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={current === 0}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-3 w-3" />
        </button>
        <span className="tabular-nums">
          {current + 1} / {pageCount}
        </span>
        <button
          className="btn btn-ghost px-1.5"
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          disabled={current >= pageCount - 1}
          aria-label="Next page"
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      </footer>
    </section>
  );
}
